import React from "react";

import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";

import { ClickOuterWrapper } from "studio-design-system";

import * as styles from "./ProfileMenu.css";

/**
 * Styled container for profile pop-up menu
 * Uses ClickOuterWrapper to allow close on outer click
 */
export const ProfileMenu = ({
  isOpen,
  children,
  exceptions,
  darkTheme,
  onOutsideClick,
}: {
  isOpen: boolean;
  exceptions: React.MutableRefObject<null>[];
  children?: React.ReactNode;
  darkTheme?: boolean;
  onOutsideClick: () => void;
}): React.ReactElement => (
  <ClickOuterWrapper isOpen={isOpen} onOutsideClick={onOutsideClick} exceptions={exceptions}>
    <div
      className={classNames([
        styles.profileMenuBase,
        darkTheme && styles.profileMenuDarkTheme,
        isOpen && styles.profileMenuOpen,
      ])}
    >
      {children}
    </div>
  </ClickOuterWrapper>
);

/**
 * Link component styled for pop-up; detects if on own pathname and styles accordingly
 */
export const ProfileMenuLink = ({
  pathname,
  label,
  children,
  darkTheme,
}: {
  pathname: string;
  label?: string;
  children?: React.ReactElement;
  darkTheme?: boolean;
  "data-cy"?: string;
}): React.ReactElement => {
  const router = useRouter();
  const isOnPath = router.pathname.includes(pathname);
  return (
    <a
      href={pathname}
      target={"__blank"}
      rel="noreferrer"
      className={classNames(
        darkTheme ? styles.darkProfileMenuButton : styles.profileMenuButton,
        isOnPath && styles.profileMenuItemSelected,
      )}
    >
      <div>{children || <span className={styles.profileMenuLinkText}>{label}</span>}</div>
    </a>
  );
};

/**
 * Styled separator line used between buttons in pop up
 */
export const ProfileMenuSeparator = ({
  darkTheme,
}: {
  darkTheme?: boolean;
}): React.ReactElement => (
  <div className={darkTheme ? styles.darkProfileMenuSeparator : styles.profileMenuSeparator} />
);
